import { MDSSidebarWidths } from "@/mds/foundations";
import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import React from "react";
import styled from "@emotion/styled";
import { SidebarDropdownMenu } from "@/components/layout/components/sidebar/sidebar-dropdown-menu";
import { SidebarContents } from "@/components/layout/components/sidebar/sidebar-contents";
import {
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSIconKind,
  MDSMenuDropdown,
  MDSMenuDropdownSlotName,
  MDSTooltip,
  MDSTooltipPlacement,
} from "@/mds";
import {
  StyledAccountInfoWrapper,
  StyledSidebarWrapper,
} from "@/components/layout/components/sidebar/sidebar.styles";
import { SidebarAccountInfo } from "@/components/layout/components/sidebar/sidebar-account-info";
import { Pressable } from "react-aria";
import { getShortcutTooltipConfig } from "@/modules/keyboard-shortcuts/tooltip";

interface SidebarExpandedProps {
  setIsImportModalOpen: (b: boolean) => void;
  toggleSidebar: () => void;
  shouldHideSidebarToggle: boolean;
}

export const SidebarExpanded = observer(
  ({ setIsImportModalOpen, toggleSidebar, shouldHideSidebarToggle }: SidebarExpandedProps) => {
    const { store } = useAppStore();
    const { profileDisplayName, profilePhotoUrl } = store.account.myAccount;

    const stopPropagation = (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
    };

    return (
      <StyledSidebarExpandedWrapper onClick={stopPropagation}>
        <MDSMenuDropdown placement="bottom">
          <div slot={MDSMenuDropdownSlotName.Trigger}>
            <Pressable>
              <StyledAccountInfoWrapper role="button" data-test-id="sidebar-account-trigger">
                <SidebarAccountInfo
                  profileDisplayName={profileDisplayName}
                  profilePhotoUrl={profilePhotoUrl}
                />
              </StyledAccountInfoWrapper>
            </Pressable>
          </div>
          <SidebarDropdownMenu
            displayAccountInfo
            slot={MDSMenuDropdownSlotName.Menu}
            setIsImportModalOpen={setIsImportModalOpen}
          />
        </MDSMenuDropdown>
        <SidebarContents />
        {!shouldHideSidebarToggle && (
          <MDSTooltip
            config={getShortcutTooltipConfig({
              action: "toggle_sidebar",
              label: "Close sidebar",
              placement: MDSTooltipPlacement.Top,
            })}
          >
            <StyledSidebarToggleButton
              size={MDSIconButtonSize.Medium}
              iconKind={MDSIconKind.ArrowLeftToLine}
              variant={MDSIconButtonVariant.OnChrome}
              onClick={toggleSidebar}
            />
          </MDSTooltip>
        )}
      </StyledSidebarExpandedWrapper>
    );
  }
);

const StyledSidebarExpandedWrapper = styled(StyledSidebarWrapper)`
  width: ${MDSSidebarWidths.expanded}px;
  height: 100%;
  flex: 1;
  min-height: 0;
`;
StyledSidebarExpandedWrapper.displayName = "StyledSidebarExpandedWrapper";

const StyledSidebarToggleButton = styled(MDSIconButton)`
  position: absolute;
  bottom: 0;
  right: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  background-color: ${({ theme }) => theme.colors.background.button.navigation.default};
  border-radius: ${({ theme }) => theme.borderRadius.none};
  border-top-left-radius: ${({ theme }) => theme.borderRadius.xlarge};
  border-bottom-left-radius: ${({ theme }) => theme.borderRadius.xlarge};

  &[data-hovered]:not([data-disabled]) {
    background-color: ${({ theme }) => theme.colors.background.button.navigation.hover};
  }
`;
StyledSidebarToggleButton.displayName = "StyledSidebarToggleButton";
